import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { FaRegNewspaper, FaBullseye, FaImages, FaMapMarkerAlt, FaEnvelope, FaPhone, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import api from '../services/api';
import LazyImage from '../components/LazyImage';
import {
  Page,
  Hero,
  HeroText,
  HeroButton,
  HeroButtonSecondary,
  HeroActions,
  HeroHighlights,
  Dots,
  Dot,
  SlideArrow,
  Section,
  Grid,
  Card,
  ContactLayout,
  ContactCard,
  ContactField,
  ContactButton,
   ContactDetailRow,
   ContactDetailIcon,
   ContactDetailText,
} from './HomeStyles';

const HomeLanding = () => {
  const [data, setData] = useState(null);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeError, setHomeError] = useState(null);
  const [index, setIndex] = useState(0);
  const [newsPosts, setNewsPosts] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const images = useMemo(
    () => (Array.isArray(data?.hero?.images) ? data.hero.images : []),
    [data]
  );

  useEffect(() => {
    let mounted = true;
    setHomeLoading(true);
    setHomeError(null);
    api.home.get()
      .then((homeData) => {
        if (!mounted) return;
        setData(homeData);
      })
      .catch((err) => {
        if (!mounted) return;
        setHomeError(err.message);
      })
      .finally(() => {
        if (!mounted) return;
        setHomeLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setNewsLoading(true);
    setNewsError(null);
    api.news.listPosts({ page: 1, limit: 3 })
      .then((newsList) => {
        if (!mounted) return;
        setNewsPosts(newsList.items || []);
      })
      .catch((err) => {
        if (!mounted) return;
        setNewsError(err.message);
      })
      .finally(() => {
        if (!mounted) return;
        setNewsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSent(null);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        throw new Error('Contact form is not configured yet.');
      }

      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: contactForm.name,
          from_email: contactForm.email,
          message: contactForm.message,
          fal_contact_email: data?.contactInfo?.email || '',
        },
        { publicKey }
      );

      setSent('Message sent successfully.');
      setContactForm({ name: '', email: '', message: '' });
    } catch (err) {
      setSent(err.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const heroBgImage = (images.length > 0)
    ? images[index]
    : (data?.hero?.backgroundImage || undefined);

  const previousSlide = () => {
    setIndex((current) => (current - 1 + images.length) % images.length);
  };

  const nextSlide = () => {
    setIndex((current) => (current + 1) % images.length);
  };

  return (
    <Page>
      <Hero
        $bgImage={heroBgImage}
      >
        {homeLoading && !data ? (
          <div
            style={{
              minHeight: '260px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Oval
              height={48}
              width={48}
              color="#2E8B57"
              secondaryColor="#CDEAD7"
            />
          </div>
        ) : (
          <>
            <HeroText>
              <h1>{data?.hero?.title || 'Food Authority Liberia (FAL)'}</h1>
              <p>
                {data?.hero?.subtitle ||
                  'Ensuring safety and quality across the food supply chain.'}
              </p>
              <HeroHighlights>
                <li>Regulates food and feed safety across Liberia.</li>
                <li>Licenses and supports food premises and businesses.</li>
                <li>Promotes standards aligned with Codex guidance.</li>
              </HeroHighlights>
              <HeroActions>
                <HeroButton as={Link} to="/business/login">
                  Food Premises Login
                </HeroButton>
                <HeroButtonSecondary as={Link} to="/fa-act">
                  Read the FAL Act
                </HeroButtonSecondary>
              </HeroActions>
            </HeroText>
            {images.length > 0 && (
              <>
                {images.length > 1 ? (
                  <>
                    <SlideArrow type="button" $direction="prev" aria-label="Previous slide" onClick={previousSlide}>
                      <FaChevronLeft />
                    </SlideArrow>
                    <SlideArrow type="button" $direction="next" aria-label="Next slide" onClick={nextSlide}>
                      <FaChevronRight />
                    </SlideArrow>
                  </>
                ) : null}
                <Dots>
                  {images.map((_, i) => (
                    <Dot
                      key={i}
                      active={i === index}
                      onClick={() => setIndex(i)}
                    />
                  ))}
                </Dots>
              </>
            )}
          </>
        )}
      </Hero>

      <Section>
        <h2 style={{ color: '#1B5E20', margin: '0 0 12px 0' }}>About Us</h2>
        <p style={{ margin: 0, maxWidth: 720 }}>
          {data?.snippets?.about || 'Learn more about our mission and vision.'}
        </p>
        <div style={{ marginTop: 12 }}>
          <Link to="/about">Read more</Link>
        </div>
      </Section>

      <Section $variant="cta">
        <h2 style={{ color: '#FFFFFF', margin: '0 0 12px 0' }}>Latest News</h2>
        {newsLoading && newsPosts.length === 0 ? (
          <div
            style={{
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Oval
              height={40}
              width={40}
              color="#2E8B57"
              secondaryColor="#CDEAD7"
            />
          </div>
        ) : newsError && newsPosts.length === 0 ? (
          <div style={{ padding: 20 }}>Error loading news: {newsError}</div>
        ) : (
          <Grid>
            {newsPosts.map((post) => (
              <Card key={post._id}>
                <Link
                  to={`/news/${post._id}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <div style={{ marginBottom: 8 }}>
                    {post.images?.[0] ? (
                      <LazyImage
                        src={post.images[0]}
                        alt={post.title}
                        style={{
                          width: '100%',
                          height: 140,
                          objectFit: 'cover',
                          borderRadius: 10,
                        }}
                      />
                    ) : null}
                  </div>
                  <h3>{post.title}</h3>
                  <p>
                    {post.description?.slice(0, 100) || ''}
                    {post.description?.length > 100 ? '…' : ''}
                  </p>
                  <div style={{ marginTop: 10 }}>
                    <span>Read more →</span>
                  </div>
                </Link>
              </Card>
            ))}
            {newsPosts.length === 0 && !newsError && (
              <Card>
                <p>No news posts available yet.</p>
              </Card>
            )}
          </Grid>
        )}
      </Section>

      <Section>
        <h2 style={{ color: '#1B5E20', margin: '0 0 12px 0' }}>Discover</h2>
        <Grid>
          <Card>
            <div style={{ fontSize: 28, color: '#1B5E20', marginBottom: 8 }}>
              <FaRegNewspaper />
            </div>
            <h3>News & Media</h3>
            <p>
              {data?.snippets?.news ||
                'Get the latest updates and announcements.'}
            </p>
            <div style={{ marginTop: 10 }}>
              <Link to="/news">View news →</Link>
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 28, color: '#1B5E20', marginBottom: 8 }}>
              <FaBullseye />
            </div>
            <h3>Objectives</h3>
            <p>
              {data?.snippets?.objectives ||
                'Our key objectives and strategic goals.'}
            </p>
            <div style={{ marginTop: 10 }}>
              <Link to="/objectives">Explore objectives →</Link>
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 28, color: '#1B5E20', marginBottom: 8 }}>
              <FaImages />
            </div>
            <h3>Gallery</h3>
            <p>{data?.snippets?.gallery || 'Browse our image gallery.'}</p>
            <div style={{ marginTop: 10 }}>
              <Link to="/gallery">Open gallery →</Link>
            </div>
          </Card>
        </Grid>
      </Section>

      <Section $variant="muted">
        <h2 style={{ color: '#1B5E20', margin: '0 0 16px 0' }}>Contact</h2>
        <ContactLayout>
          <ContactCard>
            <h3>Send us a message</h3>
            <form onSubmit={handleContactSubmit}>
              <ContactField>
                <label htmlFor="home-contact-name">Name</label>
                <input
                  id="home-contact-name"
                  name="name"
                  value={contactForm.name}
                  onChange={handleContactChange}
                />
              </ContactField>
              <ContactField>
                <label htmlFor="home-contact-email">Email</label>
                <input
                  id="home-contact-email"
                  name="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                />
              </ContactField>
              <ContactField>
                <label htmlFor="home-contact-message">Message</label>
                <textarea
                  id="home-contact-message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                />
              </ContactField>
              <ContactButton type="submit" disabled={sending}>
                {sending ? 'Sending…' : 'Send Message'}
              </ContactButton>
              {sent ? <div style={{ marginTop: 10 }}>{sent}</div> : null}
            </form>
          </ContactCard>

          <ContactCard $tone="details">
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>Contact details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <ContactDetailRow>
                <ContactDetailIcon>
                  <FaMapMarkerAlt />
                </ContactDetailIcon>
                <ContactDetailText>
                  <h3>Address</h3>
                  <p>{data?.contactInfo?.address || '—'}</p>
                </ContactDetailText>
              </ContactDetailRow>
              <ContactDetailRow>
                <ContactDetailIcon>
                  <FaEnvelope />
                </ContactDetailIcon>
                <ContactDetailText>
                  <h3>Email</h3>
                  <p>{data?.contactInfo?.email || '—'}</p>
                </ContactDetailText>
              </ContactDetailRow>
              <ContactDetailRow>
                <ContactDetailIcon>
                  <FaPhone />
                </ContactDetailIcon>
                <ContactDetailText>
                  <h3>Phone</h3>
                  <p>{data?.contactInfo?.phone || '—'}</p>
                </ContactDetailText>
              </ContactDetailRow>
            </div>
          </ContactCard>
        </ContactLayout>
      </Section>
    </Page>
  );
};

export default HomeLanding;
